<template>
    <Modal
        :opened="opened"
        class="ModalIssues"
        #body
        @close="handleDismiss"
    >
        <div class="Header flex row-stretch-center">
            <div class="Title flex row-left-center">
                <WText
                    tag="span"
                    class="mb-0"
                    :size="22"
                >
                    {{ $t('imports.create.issues.title') }}
                </WText>
            </div>
            <WIcon
                icon="close"
                :size="24"
                clickable
                @click="handleDismiss"
            />
        </div>

        <div class="Wrapper flex row-stretch">
            <div
                v-if="count > 1"
                class="Sidebar flex col-top-stretch"
            >
                <div class="ListIssues flex grow-1 col-top-stretch">
                    <a
                        v-for="(issue, index) in issues"
                        :key="`issue-${index}`"
                        class="Issue"
                        :class="[issue.level, { active: (selected || {}).id === issue.id }]"
                        @click.prevent="handleClickIssue(issue)"
                    >
                        <WText
                            :size="14"
                            weight="semi"
                            uppercase
                            class="IssueStatus"
                            :class="[issue.level]"
                        >
                            {{ $tc(`imports.create.notifications.${issue.level}`) }}
                        </WText>

                        <WText
                            tag="span"
                            typo="span"
                            :size="14"
                        >{{ issue.message }}</WText>
                    </a>
                </div>
            </div>

            <div class="View flex-1">
                <IssueForm
                    v-if="selected"
                    v-model="selected"
                    :defaultValue="defaultValue"
                    :loading="isLoading"
                />

                <div class="flex row-right-center">
                    <WButton
                        :loading="isLoading"
                        :disabled="isLoading || !(selected || {}).resolve"
                        @click="handleSubmit"
                    >
                        {{ $t(`imports.create.issues.form.submit`) }}
                    </WButton>
                </div>
            </div>
        </div>
    </Modal>
</template>

<script>
import VueTypes from 'vue-types'
import {
    Api, apiRequest, rethrowError, notifyError,
} from '@/api'
import { Modal } from '@/components'
import IssueForm from './IssueForm.vue'

export default {
    name: 'ModalIssues',
    components: {
        IssueForm,
        Modal,
    },
    props: {
        opened: VueTypes.bool.def(false),
        importId: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
        row: VueTypes.any,
        col: VueTypes.any,
        selectGroups: VueTypes.any,
        entity: VueTypes.string,
        columns: VueTypes.array.def([]),
    },
    data () {
        return {
            selected: undefined,
            isLoading: false,
        }
    },
    computed: {
        count () {
            const { data } = this.row || {}
            const { issuesCount } = data || {}
            return issuesCount
        },
        issues () {
            const { data } = this.row || {}
            const { issues } = data || {}
            // An issue type (edit, select...) is not the type of its field,
            // which is what decides the input to edit the value with.
            const fieldTypes = this.columns
                .reduce((acc, column) => [...acc, ...(column.children || [column])], [])
                .reduce((acc, { field, type }) => ({ ...acc, [field]: type }), {})
            return [...(issues.row || []), ...(issues.cell || [])].map((issue, id) => ({ ...issue, id: `${id}-${issue.field}`, fieldType: fieldTypes[issue.field] }))
        },
        defaultValue () {
            const { data } = this.row || {}
            const { field } = this.selected || {}
            return data[field]
        },
    },
    watch: {
        opened (value) {
            if (value) this.initialize()
        },
        // eslint-disable-next-line func-names
        'row.data': function (value) {
            if (value) this.initialize()
        },
    },
    mounted () {
        this.initialize()
    },
    methods: {
        initialize () {
            if (!this.count) {
                this.selected = this.col
                return
            }
            const { field } = this.col || {}
            this.selected = this.issues.find(issue => issue.field === field) || this.issues[0]
        },
        handleClickIssue (issue) {
            this.selected = issue
        },
        handleDismiss () {
            this.$emit('onCancel')
        },
        handleSubmit () {
            const { resolve } = this.selected
            this.resolveIssue(resolve)
        },
        async resolveIssue (row) {
            this.isLoading = true
            apiRequest(Api().imports.editSource(this.importId, {
                type: 'edit',
                row,
                ...(this.entity ? { entity: this.entity } : {}),
                ...((this.selected || {}).type === 'conflict' ? { resolveConflict: true } : {}),
            }))
                .then(({ data: issues }) => (this.$emit('onResolve', { row: { ...this.row.data, ...row }, issues })))
                .catch(rethrowError(notifyError()))
                .finally(() => (this.isLoading = false))
        },
    },
}
</script>
<style lang="scss" scoped>
.Modal {
    &::v-deep .Dialog {
        max-width: 900px;
        overflow: initial;
    }

    &::v-deep .Body {
        padding: 0;
    }
}

.Wrapper {
    min-height: 500px;
    height: 100%;
}

.Header {
    padding: 20px;
    border-bottom: 1px solid $gray-05;
}

.Sidebar {
    width: 100%;
    max-width: 280px;
    background: $gray-08;
    border-radius: $border-radius;
    border-right: 1px solid $gray-05;
    box-sizing: border-box;

    .Title {
        padding: 20px;
        border-bottom: 1px solid $gray-05;
        box-sizing: border-box;
    }
}

.View {
    padding: 20px;
}

.ListIssues {
    max-height: 650px;
    overflow: auto;
}

.Issue {
    padding: 10px 20px;
    border-bottom: 1px solid $gray-05;

    &:hover, &.active {
        background: rgba($accent-blue, .1);
        transition: all 250ms ease;
    }

    span {
        width: 100%;
    }

    &Status {
        &.success {
            color: $success;
        }
        &.error {
            color: $danger;
        }
        &.warning {
            color: $warning;
        }
        &.info {
            color: $info;
        }
    }
}
</style>
